/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import styled, { css } from 'styled-components'
import { Formik, FormikConfig, FormikProps } from 'formik'
import { Form as FormBs, FormProps as FormPropsBs } from '@bootstrap-styled/v4'
import { gridMixins } from '@bootstrap-styled/css-mixins'

const { makeRow, makeContainer } = gridMixins

// type definition
type PassFormProps = Omit<FormPropsBs, 'onSubmit' | 'onReset' | 'ref' | 'children'>
type PassFormConfig<V> = Omit<FormikConfig<V>, 'component' | 'render'>
type FormDefaults = Required<Pick<FormProps, 'noValidate' | 'enableReinitialize'>>

export interface FormProps<V = any> extends PassFormProps, PassFormConfig<V> {
    formRef?: React.Ref<HTMLFormElement>
    isFilter?: boolean
}

class FormUnstyled extends React.Component<FormProps> {
    public static defaultProps: FormDefaults = {
        noValidate: true,
        enableReinitialize: true
    }

    public render(): React.ReactNode {
        const {
            id,
            className,
            formRef,
            children,
            onSubmit,
            isFilter,
            // rest formik config
            onReset,
            initialValues,
            initialStatus,
            initialErrors,
            validationSchema,
            validate,
            innerRef,
            validateOnChange,
            validateOnBlur,
            validateOnMount,
            isInitialValid,
            enableReinitialize,
            // native form attributes
            ...attributes
        } = this.props

        // formik config
        const config = {
            onReset,
            initialValues,
            initialStatus,
            initialErrors,
            validationSchema,
            validate,
            innerRef,
            validateOnChange,
            validateOnBlur,
            validateOnMount,
            isInitialValid,
            enableReinitialize
        }

        return (
            <Formik
                onSubmit={async (values, actions) => {
                    /* setTimeout(() => {
                        actions.setSubmitting(false)
                    }, 400) */

                    return onSubmit(values, actions)
                }}
                {...config}
            >
                {(props: FormikProps<typeof initialValues>) => {
                    const invalidForm = !props.isValid || props.dirty
                    const classes = cn(className, {
                        'was-validated': invalidForm,
                        'form-is-filter': !!isFilter
                    })

                    return (
                        <FormBs
                            id={id}
                            className={classes}
                            onSubmit={props.handleSubmit}
                            onReset={props.handleReset}
                            ref={node => {
                                if (typeof formRef === 'function') {
                                    // eslint-disable-next-line react/no-find-dom-node
                                    formRef(ReactDOM.findDOMNode(node) as HTMLFormElement | null)
                                }
                            }}
                            {...attributes}
                        >
                            {typeof children === 'function' && 'call' in children
                                ? children(props)
                                : children}
                        </FormBs>
                    )
                }}
            </Formik>
        )
    }
}

const Form = styled(FormUnstyled)`
    /* Styles for form grid */
    && .form-row {
        ${props =>
            css`
                ${makeRow(
                    props.theme['$enable-grid-classes'],
                    props.theme['$form-grid-gutter-width']
                )}
            `}

        > .col,
        > [class*='col-'] {
            ${props =>
                css`
                    ${makeContainer(
                        props.theme['$enable-grid-classes'],
                        props.theme['$form-grid-gutter-width']
                    )}
                `}
        }
    }

    && .col,
    && [class*='col-'] {
        > .custom-control {
            margin-right: 0;
        }

        .btn.btn-expand {
            height: 100%;
        }
    }

    /* Styles for content with inside checkbox and radiobutton */
    && .form-check {
        > .custom-control {
            &:last-of-type {
                margin-right: 0;
            }
        }
    }
    /* Styles for filter form */
    &.form-is-filter {
        .form-group {
            margin-bottom: 20px; /* TODO: Agregar la variable de settings bs4 $form-group-filter-margin-bottom*/
        }
    }
`

export default Form
